import React, { useState } from 'react';
import { Select, Empty, Grid } from 'antd';
import { isEmpty, startCase } from 'lodash';
// v2 migration: import the shared Recharts chart from designSystem/.
// Legacy BizCharts copy at './components/DualAxisAreaChart' stays for safety
// rollback but is no longer wired in. Deletable once all Campaign Details tabs
// (Transactions, SignUps, etc.) also migrate off their local BizCharts copies.
import DualAxisAreaChart from '../../../../../../../shared/designSystem/components/DualAxisAreaChart';
import moment from 'moment-timezone';
import { MenuItemTextColor } from '../../../../../../../shared/globalStyling/styledText';

const { Option } = Select;

const SummaryChart = ({
  reportData,
  showTransactions,
  showAttribution,
  showSessions = true,
  chartsBuilt,
  dataSummaryRangeDate,
  currencyFormat,
}) => {
  // map the chart and return the dates with total summary
  let validOptions = ['clicks', 'impressions', 'ctr'];

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const walkInsOptions = ['walkIns', 'observations'];
  const transactionOptions = ['transactionTotal', 'transactionCount'];
  const sessionOptions = ['sessions'];

  // dataSummaryRangeDate is owned by SummaryDetailBar (see its useEffect that
  // filters by row.date / UTC unix). The chart consumes the prop directly —
  // no useByDateFilter, no rangeKey↔rangeType bridge. The page-level
  // SummaryRangeSelector + bySummaryRangeDate is the single source of truth.

  if (showTransactions) {
    validOptions = validOptions.concat(transactionOptions);
  }

  if (showAttribution) {
    validOptions = validOptions.concat(walkInsOptions);
  }

  if (showSessions) {
    validOptions = validOptions.concat(sessionOptions);
  }

  const [leftY, setLeftY] = useState(startCase(validOptions[0]));
  const [rightY, setRightY] = useState(startCase(validOptions[1]));

  // DateSelector component removed — page-level SummaryRangeSelector owns the
  // range control now. The chart consumes dataSummaryRangeDate verbatim; the
  // parent (SummaryDetailBar) owns the windowing logic so KPI percentages
  // match develop while xAxis labels stay UTC.

  const YAxisSelector = ({ displayDateSelector, displayRightSelector, data }) => {
    const keys = Object.keys(data[0])
      .filter((el) => validOptions.includes(el))
      .map((option) => {
        return option === 'cpc' || option === 'cpm' || option === 'ctr' || option === 'ecpm'
          ? option.toUpperCase()
          : startCase(option);
      })
      .filter((el) => el !== 'Creativeurl');

    return (
      <div className="campaign-summary__axis-row">
        <div className="campaign-summary__axis-field">
          <span className="campaign-summary__axis-label mono-eyebrow">
            <span className="campaign-summary__axis-dot campaign-summary__axis-dot--left" />
            Left axis
          </span>
          <Select
            onSelect={(value) => setLeftY(value)}
            className="campaign-summary__axis-select"
            defaultValue={leftY}
            getPopupContainer={(trigger) => trigger.parentNode}
            popupClassName="mjl-ds"
          >
            {keys
              .filter((op) => op !== rightY)
              .map((option) => {
                return (
                  <Option key={option} value={option}>
                    <MenuItemTextColor text={option} color={'#7dafff'} />
                  </Option>
                );
              })}
          </Select>
        </div>
        {displayDateSelector ? (
          <div className="campaign-summary__axis-field campaign-summary__axis-field--center">
            <DateSelector data={reportData} screens={screens} />
          </div>
        ) : null}
        {displayRightSelector ? (
          <div className="campaign-summary__axis-field campaign-summary__axis-field--right">
            <span className="campaign-summary__axis-label mono-eyebrow">
              <span className="campaign-summary__axis-dot campaign-summary__axis-dot--right" />
              Right axis
            </span>
            <Select
              onSelect={(value) => {
                setRightY(value);
              }}
              className="campaign-summary__axis-select"
              defaultValue={rightY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {keys
                .filter((op) => op !== leftY)
                .map((option) => {
                  return (
                    <Option key={option} value={option}>
                      <MenuItemTextColor text={option} color={'#92d5a7'} />
                    </Option>
                  );
                })}
            </Select>
          </div>
        ) : null}
      </div>
    );
  };

  // Empty state — chart has no data. Lives inside the unified performance
  // card, so no wrapping AntD Card needed; just a centered empty message.
  const emptyCharts = isEmpty(reportData);
  if (emptyCharts)
    return (
      <div
        className="campaign-summary__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  // Unified card renders the chart directly — no AntD Tabs / nested Card.
  // "Day Of Week" view was rarely used; removed for this pass. Can be
  // reintroduced later as a segmented toggle if needed.
  return (
    <div className="campaign-summary__chart">
      <div className="campaign-summary__chart-head">
        <div className="campaign-summary__chart-eyebrow mono-eyebrow">
          Trend
          {chartsBuilt && (
            <span style={{ marginLeft: 8, fontSize: 10, opacity: 0.7 }}>
              Updated {moment(chartsBuilt).calendar()}
            </span>
          )}
        </div>
        {!isEmpty(reportData) && (
          <YAxisSelector
            displayRightSelector={true}
            displayDateSelector={false}
            data={reportData}
          />
        )}
      </div>
      <div className="campaign-summary__chart-body">
        {!isEmpty(reportData) && dataSummaryRangeDate.length !== 0 && validOptions.length > 1 && (
          <DualAxisAreaChart
            leftY={isEmpty(leftY) ? validOptions[0] || '' : leftY}
            rightY={isEmpty(rightY) ? validOptions[1] || '' : rightY}
            dualChartData={dataSummaryRangeDate}
          />
        )}
      </div>
    </div>
  );
};

export default SummaryChart;
